import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { Box } from '@mui/material';
import CardTemplate from './CardTemplate';
import { useChartTheme } from '../overview/plotTheme';

// Ranges for the two y axes so their zeros sit at the same height (values may be negative, e.g. solar export)
const alignedRanges = (values1, values2) => {
  const bounds = [values1, values2].map(values => [Math.min(0, ...values), Math.max(0, ...values)]);
  // Fraction of the plot height below zero, averaged so both axes get room when one is negative and the other positive
  const fractions = bounds.map(([lo, hi]) => (hi - lo > 0 ? -lo / (hi - lo) : 0));
  const p = (fractions[0] + fractions[1]) / 2;
  return bounds.map(([lo, hi]) => {
    let span = Math.max(p > 0 ? -lo / p : 0, p < 1 ? hi / (1 - p) : 0);
    if (span === 0) span = 1;
    span *= 1.05; // Small headroom
    return [-p * span, (1 - p) * span];
  });
};

// Same colours on every chart so energy vs. peak demand reads the same everywhere
const DEFAULT_ENERGY_COLOR = "#3B82F6";
const DEFAULT_EXPORT_COLOR = "#10B981";
const DEFAULT_POWER_COLOR = "#F97316";

const PlotCard = ({
  cardTitle,
  cardSubtitle,
  seriesEnergy,
  seriesPeakPower,
  energyColor = DEFAULT_ENERGY_COLOR,
  exportColor = DEFAULT_EXPORT_COLOR,
  powerColor = DEFAULT_POWER_COLOR,
  binMs = 3600 * 1000, // Duration of one bar (hour / day / month)
  windowMs = 24 * binMs, // Time span shown when there is no data
  xTickFormat, // Optional: one tick per bar with this format (e.g. "%b %d"), otherwise Plotly picks the ticks
  sideBySide = false, // Import and export bars next to each other, otherwise import above zero and export below
}) => {

  const { mode, colors } = useChartTheme();

  useEffect(() => {
    let _data = [];
    // Timestamps are timezone-naive "YYYY-MM-DD HH:mm:ss", parse as UTC to match Plotly
    const toMs = (x) => typeof x === "number" ? x : Date.parse(String(x).replace(" ", "T") + "Z");
    // Back to a naive string, Plotly applies a timezone shift to numeric ms on date axes
    const toStr = (ms) => new Date(ms).toISOString().slice(0, 23).replace("T", " ");

    if (seriesEnergy && seriesEnergy.length > 0 && seriesEnergy[0].x && seriesEnergy[0].x.length > 0) {
      // Either import above zero and export below zero (export drawn negative, shown positive on hover),
      // or both positive with import in the left half of the interval and export in the right half
      const _barWidth = sideBySide ? binMs * 0.4 : binMs * 0.8;
      const _importOffset = binMs * 0.1; // Timestamps mark the interval start, so draw bars inside [t, t + binMs]
      const _exportOffset = sideBySide ? binMs * 0.5 : binMs * 0.1;
      seriesEnergy.map((dataset, index) => {
        const _suffix = index === 0 ? "" : " " + (index + 1);
        const _showLegend = index === 0;
        const _bar = {
          x: dataset.x,
          type: "bar",
          width: _barWidth, // Fixed width so gaps or a single point don't distort the bars
          showlegend: _showLegend,
        };
        _data.push({
          ..._bar,
          y: dataset.yImport,
          offset: _importOffset,
          name: "Import" + _suffix,
          legendgroup: "import",
          marker: { color: energyColor, cornerradius: 3 },
          hovertemplate: "%{x}<br>Import %{y:,.1f} kWh<extra></extra>",
        });
        _data.push({
          ..._bar,
          y: sideBySide ? dataset.yExport : dataset.yExport.map(y => (y == null ? null : -y)),
          offset: _exportOffset,
          customdata: dataset.yExport,
          name: "Export" + _suffix,
          legendgroup: "export",
          marker: { color: exportColor, cornerradius: 3 },
          hovertemplate: "%{x}<br>Export %{customdata:,.1f} kWh<extra></extra>",
        });

        // Plotly doesn't draw zero-height bars, so mark zero values with a thin line at y = 0
        const _zeroLine = (values, offset, color) => {
          const _zeroX = [];
          const _zeroY = [];
          dataset.x.forEach((x, i) => {
            const _t = toMs(x);
            if (values[i] === 0 && !isNaN(_t)) {
              _zeroX.push(toStr(_t + offset), toStr(_t + offset + _barWidth), null);
              _zeroY.push(0, 0, null);
            }
          });
          if (_zeroX.length > 0) {
            _data.push({
              x: _zeroX,
              y: _zeroY,
              type: "scatter",
              mode: "lines",
              line: { color: color, width: 4 },
              showlegend: false,
              hoverinfo: "skip",
            });
          }
        };
        if (sideBySide) {
          _zeroLine(dataset.yImport, _importOffset, energyColor);
          _zeroLine(dataset.yExport, _exportOffset, exportColor);
        } else {
          // Shared slot, only mark intervals with no flow in either direction
          const _noFlow = dataset.x.map((x, i) => {
            const _imp = dataset.yImport[i];
            const _exp = dataset.yExport[i];
            return (_imp === 0 && !_exp) || (_exp === 0 && !_imp) ? 0 : null;
          });
          _zeroLine(_noFlow, _importOffset, energyColor);
        }
      });
    }
    
    // console.log(seriesPeakPower);
    if (seriesPeakPower && seriesPeakPower.length && seriesPeakPower[0].x && seriesPeakPower[0].x.length > 0) {

      seriesPeakPower.map((dataset, index) => {
        let _name = "Peak power " + (index + 1);
        let _showLegend = false;
        if (index == 0) {
          _name = "Peak power";
          _showLegend = true;
        }
        _data.push({
          // Timestamps mark the interval start, shift to the middle so points line up with the energy bars
          x: dataset.x.map(x => { const _t = toMs(x); return isNaN(_t) ? x : toStr(_t + binMs * 0.5); }),
          y: dataset.y,
          text: dataset.x, // Show the original interval timestamp on hover
          hovertemplate: "%{text}<br>%{y:,.1f} kW<extra></extra>",
          type: "scatter",
          mode: "lines+markers",
          name: _name,
          yaxis: "y2",
          line: { color: powerColor, width: 2.5 },
          marker: { color: powerColor, size: 5, line: { color: colors.markerOutline, width: 1 } },
          fill: "none",
          // line: { shape: "spline" },
          // stackgroup: "power",
          showlegend: _showLegend,
          // hoverinfo: "none",
        })
      });

    }

    // With a single timestamp Plotly zooms the date axis to sub-milliseconds, so pad the range manually
    const _times = _data.flatMap(trace => trace.x.filter(x => x !== null).map(toMs)).filter(t => !isNaN(t));
    const _tMin = Math.min(..._times);
    const _tMax = Math.max(..._times);
    if (_times.length === 0) {
      // No data, show an empty grid over the window ending now (local wall-clock time, same as the data)
      const _now = Date.now() - new Date().getTimezoneOffset() * 60 * 1000;
      setXRange([toStr(_now - windowMs), toStr(_now)]);
      // Placeholder traces (single null point) so the legend and right axis still render
      _data = [
        { x: [toStr(_now)], y: [null], type: "bar", name: "Import", marker: { color: energyColor }, showlegend: true },
        { x: [toStr(_now)], y: [null], type: "bar", name: "Export", marker: { color: exportColor }, showlegend: true },
        { x: [toStr(_now)], y: [null], type: "scatter", mode: "lines+markers", name: "Peak power", yaxis: "y2", line: { color: powerColor }, showlegend: true },
      ];
    } else {
      setXRange(_tMax - _tMin < binMs ? [toStr(_tMin - 3 * binMs), toStr(_tMax + 3 * binMs)] : undefined);
    }

    // Align zeros only when both axes have data, otherwise let Plotly autorange
    const _values = (isY2) => _data.filter(trace => (trace.yaxis === "y2") === isY2)
      .flatMap(trace => trace.y).filter(y => typeof y === "number");
    const _v1 = _values(false);
    const _v2 = _values(true);
    if (_times.length === 0) {
      setYRanges([[0, 1], [0, 1]]); // Placeholder scale for the empty grid
    } else if (_v1.length > 0 && _v2.length > 0) {
      setYRanges(alignedRanges(_v1, _v2));
    } else {
      setYRanges([undefined, undefined]);
    }

    setData(_data);
  }, [seriesEnergy, seriesPeakPower, binMs, windowMs, energyColor, exportColor, powerColor, sideBySide, mode]);

  const [data, setData] = useState([]);
  const [yRanges, setYRanges] = useState([undefined, undefined]);
  const [xRange, setXRange] = useState(undefined);

  return (
    <CardTemplate title={cardTitle} subtitle={cardSubtitle}>
      <Box sx={{
        width: "100%",
        height: 280,
      }}>
        <Plot
          data={data}
          layout={{
            autosize: true,
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            font: { family: "Roboto, Helvetica, Arial, sans-serif", size: 11, color: colors.text },
            bargap: 0,
            barmode: sideBySide ? "overlay" : "relative", // Bar positions come from the explicit width / offset
            hoverlabel: {
              bgcolor: colors.hoverBg,
              bordercolor: colors.hoverBorder,
              font: { color: colors.hoverText, size: 12 },
            },
            xaxis: {
              type: "date",
              range: xRange,
              // One tick per bin, placed mid-bar so the label sits under its bar
              ...(xTickFormat ? { dtick: binMs, tick0: new Date(binMs / 2).toISOString().slice(0, 19).replace("T", " "), tickformat: xTickFormat } : {}),
              showline: false,
              showgrid: false,
              zeroline: false,
              ticks: "",
            },
            yaxis: {
              title: { text: "Energy (kWh)", font: { size: 12, color: colors.text }, standoff: 8 },
              range: yRanges[0],
              showline: false,
              showgrid: true,
              gridcolor: colors.grid,
              zeroline: true,
              zerolinecolor: colors.zeroLine,
              automargin: true,
            },
            yaxis2: {
              title: { text: "Peak demand (kW)", font: { size: 12, color: colors.text }, standoff: 8 },
              range: yRanges[1],
              zeroline: false, // Zeros are aligned, the energy axis already draws the zero line
              overlaying: "y",
              side: "right",
              showline: false,
              showgrid: false,
              automargin: true,
            },
            margin: { l: 8, r: 8, t: 8, b: 32 },
            legend: {
              orientation: "h",
              x: 0,
              y: 1.02,
              xanchor: "left",
              yanchor: "bottom",
              bgcolor: "rgba(0,0,0,0)",
              font: { size: 12, color: colors.legend },
            },
          }}
          config={{
            displayModeBar: false, // Disable toolbar
          }}
          useResizeHandler={true} // Enable resizing
          style={{ width: "100%", height: "100%" }} // Auto-size to parent
        />
      </Box>
    </CardTemplate>
  )
}

export default PlotCard;